import { ScanSearch } from 'lucide-react'
import type { ReactNode } from 'react'

import { type Source, useCurrentFiling } from '@/modules/tools/bankruptcy'
import { useDialogActions } from '@/stores'
import { cn } from '@/utils'
import { SOURCE_VIEWER_DIALOG_ID } from './SourceViewer'

interface SourceTriggerProps {
  source?: Source
  label: string
  children: ReactNode
  className?: string
  iconClassName?: string
}

// Wraps a breakdown value or finding so it opens its page in the SourceViewer. When there is
// no source, or its page is not in scope, the content renders plainly -- no dead affordance.
// Hovering or focusing warms the page preview so the click opens instantly.
export const SourceTrigger = ({
  source,
  label,
  children,
  className,
  iconClassName,
}: SourceTriggerProps) => {
  const { openDialog } = useDialogActions()
  const filing = useCurrentFiling()
  const document = filing?.documentsById.get(source?.documentId as number)
  const page = document?.pageBboxesByNumber.get(source?.page as number)

  if (!source || !page) return <>{children}</>

  const prefetch = () => {
    const image = new Image()
    image.src = page.previewUrl
  }

  return (
    <button
      type="button"
      onClick={() =>
        openDialog(SOURCE_VIEWER_DIALOG_ID, { source, document, page, label })
      }
      onMouseEnter={prefetch}
      onFocus={prefetch}
      aria-label={`View source for ${label}`}
      className={cn(
        'group cursor-pointer inline-flex items-center gap-1 rounded underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      {children}
      <ScanSearch
        className={cn(
          'size-3 shrink-0 text-muted-foreground opacity-40 transition-opacity group-hover:opacity-100',
          iconClassName,
        )}
      />
    </button>
  )
}
